import React from 'react';

import { TooltipCustom } from 'core/chatbot/components/atoms/legacyAtoms';
import { Icon, Loading } from 'ui';
import PrerequisiteList from '../../../../components/atoms/PrerequisiteList/PrerequisiteList';
import StatCard from '../../../../components/atoms/StatCard/StatCard';
import VerticalKcTimeline from '../../../../components/atoms/VerticalKcTimeline/VerticalKcTimeline';
import type { RadarAlert } from '../../../../types/radar';
import RadarTypeTag from '../../RadarTypeTag/RadarTypeTag';
import SeverityBadge from '../../SeverityIndicator/SeverityBadge';
import './_learning-block-content.scss';
import useLearningBlockContent from './useLearningBlockContent';

type LearningBlockContentProps = {
  alert: RadarAlert;
  isMockMode?: boolean;
};

function LearningBlockContent({ alert, isMockMode }: LearningBlockContentProps): JSX.Element {
  const {
    detail,
    error,
    handleToggleRollbackInfo,
    handleToggleTimeline,
    hasRollbacks,
    hasTimeline,
    isLoading,
    isRollbackInfoExpanded,
    isTimelineExpanded,
    locationItems,
    t,
  } = useLearningBlockContent({
    annotationId: alert.id,
    isMockMode,
  });

  if (isLoading) {
    return (
      <div className="alert-detail-drawer__loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert-detail-drawer__error">
        <p>{t('chatbot:ai_analytics_student_conversation_error')}</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="alert-detail-drawer__empty">
        <p>{t('chatbot:ai_analytics_student_conversation_empty')}</p>
      </div>
    );
  }

  return (
    <div className="learning-block-content">
      <div className="learning-block-content__header">
        <div className="learning-block-content__label-row">
          <div className="learning-block-content__label-group">
            <RadarTypeTag type="learning-block" />
            <TooltipCustom placement="bottom-start" title={t('chatbot:radar_detail_learning_block_tooltip')}>
              <span>
                <Icon color="grey-03" size="md" name="info-circle" />
              </span>
            </TooltipCustom>
          </div>
          <SeverityBadge severity={alert.severity} />
        </div>
        <div className="learning-block-content__kc-info">
          <h3 className="learning-block-content__kc-statement">{detail.kcStatement}</h3>
          {locationItems.length > 0 && (
            <ul className="learning-block-content__locations">
              {locationItems.map((loc) => (
                <li key={loc} className="learning-block-content__location">
                  {loc}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="learning-block-content__stats">
        <StatCard
          iconType="success"
          iconVariant="success"
          label={t('chatbot:radar_detail_correct_activities')}
          value={`${detail.correctCount}/${detail.attemptCount}`}
        />

        {hasRollbacks && (
          <StatCard
            ariaLabel={t('chatbot:radar_detail_rollbacks')}
            iconType="refresh"
            iconVariant="rollback"
            isExpandable
            isExpanded={isRollbackInfoExpanded}
            label={t('chatbot:radar_detail_rollbacks')}
            onToggle={handleToggleRollbackInfo}
            value={detail.rollbackCount}
          >
            <PrerequisiteList prerequisites={detail.prerequisites} />
          </StatCard>
        )}

        {hasTimeline && (
          <StatCard
            ariaLabel={t('chatbot:radar_detail_kc_timeline_title')}
            iconType="restore"
            iconVariant="timeline"
            isExpandable
            isExpanded={isTimelineExpanded}
            label={t('chatbot:radar_detail_kc_timeline_title')}
            onToggle={handleToggleTimeline}
            value={detail.kcStatusTimeline.length}
          >
            <VerticalKcTimeline entries={detail.kcStatusTimeline} />
          </StatCard>
        )}
      </div>
    </div>
  );
}

export default LearningBlockContent;
